'use client';

import type { SanityImage } from '../types';
import { Media } from '../Media';
import { tv } from '@app/ui/_lib/utils';
import type { imagesLayoutValues, smallMediumLargeValues } from '../_lib/designTypesStore';
import { Container, ContainerProps } from '../Container';

export const styles = tv({
	slots: {
		base: '',
		mediaWrapper: 'rounded-lg overflow-hidden h-full [&_img]:w-full [&_img]:h-auto',
		mediaWrapper2: 'rounded-lg overflow-hidden h-full [&_img]:w-full [&_img]:h-auto',
		caption: 'text-body-sm pt-5',
	},
	variants: {
		layout: {
			full: {},
			'tall-right': {
				base: 'grid items-start gap-10 md:grid-cols-2',
				mediaWrapper: 'aspect-square [&_img]:h-full [--media-aspect-ratio:1/1]',
				mediaWrapper2: 'aspect-2/3 [&_img]:h-full [--media-aspect-ratio:2/3]',
			},
			'tall-left': {
				base: 'grid items-start gap-10 md:grid-cols-2',
				mediaWrapper: 'aspect-2/3 [&_img]:h-full [--media-aspect-ratio:2/3]',
				mediaWrapper2: 'aspect-square [&_img]:h-full [--media-aspect-ratio:1/1]',
			},
		},
	},
});

export type ImagesProps = {
	caption?: string;
	caption2?: string;
	className?: string;
	image?: SanityImage;
	video?: string;
	image2?: SanityImage;
	video2?: string;
	size?: (typeof smallMediumLargeValues)[number] | ContainerProps['size'];
	layout?: (typeof imagesLayoutValues)[number];
};

export function Images(props: ImagesProps) {
	if (!props.image) {
		return null;
	}

	const layout = !props.image2 ? 'full' : (props.layout ?? 'tall-right');
	const { base, mediaWrapper, mediaWrapper2, caption } = styles({ layout });

	return (
		<Container className={base({ className: props.className })} size={getContainerSize(props.size)} data-component='Images'>
			<figure>
				<Media
					image={props.image}
					video={props.video}
					className={mediaWrapper()}
					nextImageSizes={layout === 'full' ? '(max-width: 768px) 87vw, (max-width: 1440px) 70vw, 50vw' : undefined}
				/>
				{props.caption && <figcaption className={`${caption()}`}>{props.caption}</figcaption>}
			</figure>
			{props.image2 && (
				<figure>
					<Media
						image={props.image2}
						video={props.video2}
						className={`${mediaWrapper2()}`}
						nextImageSizes={layout === 'full' ? '(max-width: 768px) 87vw, (max-width: 1440px) 70vw, 50vw' : undefined}
					/>
					{props.caption2 && <figcaption className={`${caption()}`}>{props.caption2}</figcaption>}
				</figure>
			)}
		</Container>
	);
}

function getContainerSize(size: ImagesProps['size']): NonNullable<ContainerProps['size']> {
	switch (size) {
		case 'small':
			return 'md';
		case 'medium':
			return 'lg';
		case 'large':
			return 'default';
		default:
			return size;
	}
}
